// Schémas Zod du module « Facture » (brique 5).
//
// Cycle de vie : une facture naît en BROUILLON (sans numéro), se modifie librement, puis est
// ÉMISE (numéro FAC_AAAA-NNN attribué, document figé et INALTÉRABLE). Les schémas ci-dessous
// ne couvrent QUE la saisie du brouillon (création / mise à jour). Les transitions de statut
// (émettre, payer, annuler) passent par des routes dédiées, pas par ces schémas — on ne peut
// donc pas « forcer » un statut via le corps d'une requête.
//
// Les totaux sont calculés CÔTÉ SERVEUR (jamais reçus du navigateur) ; on réutilise la même
// fonction `calculerTotaux` que les devis (source unique de vérité, pas de doublon).
// Rappel leçon : les champs optionnels pouvant arriver à `null` sont `.nullable().optional()`.

import { z } from 'zod'

// Réutilise le calcul de totaux du module Devis (Σ lignes − remise% + port). Pas de copie.
export { calculerTotaux } from './devis'
// Unité d'affichage par ligne (brique 5D) : source unique dans devis.ts, réexportée ici.
import { UNITES_LIGNE } from './devis'
export {
  UNITES_LIGNE,
  LIBELLES_UNITE_LIGNE,
  ABREV_UNITE_LIGNE,
  abrevUniteLigne,
  type UniteLigne,
} from './devis'

export const FACTURE_STATUTS = ['brouillon', 'emise', 'payee', 'annulee'] as const
export type FactureStatut = (typeof FACTURE_STATUTS)[number]

export const LIBELLES_STATUT_FACTURE: Record<FactureStatut, string> = {
  brouillon: 'Brouillon',
  emise: 'Émise',
  payee: 'Payée',
  annulee: 'Annulée',
}

const dateISO = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ.')

export const ligneFactureSchema = z.object({
  // Présent si la ligne vient du catalogue ; absent/null pour une ligne libre.
  articleId: z.string().uuid("Identifiant d'article invalide.").nullable().optional(),
  designation: z
    .string()
    .trim()
    .min(1, 'La désignation est obligatoire.')
    .max(300, 'Désignation trop longue (300 caractères max).'),
  quantite: z
    .number()
    .positive('La quantité doit être supérieure à 0.')
    .max(1_000_000, 'Quantité trop grande.'),
  prixUnitaire: z
    .number()
    .min(0, 'Le prix ne peut pas être négatif.')
    .max(1_000_000, 'Prix trop grand.'),
  // Unité d'affichage facultative (brique 5D). Vide = aucune unité imprimée. `null` toléré.
  unite: z.enum(UNITES_LIGNE).nullable().optional(),
  ordre: z.number().int().nullable().optional(),
})
export type LigneFactureInput = z.infer<typeof ligneFactureSchema>

// Création d'un brouillon. Pas de `statut` : une facture démarre TOUJOURS en brouillon.
// `tvaPct` est optionnel : s'il est absent, le service applique le défaut du régime du compte
// (0 en franchise 293 B ; taux standard au réel). En franchise, toute valeur > 0 est ramenée à 0.
export const factureCreateSchema = z.object({
  clientId: z.string().uuid('Client invalide.').nullable().optional(),
  dateFacture: dateISO,
  dateEcheance: dateISO.nullable().optional(),
  remise: z
    .number()
    .min(0, 'La remise ne peut pas être négative.')
    .max(100, 'La remise en % ne peut pas dépasser 100.')
    .default(0),
  fraisPort: z.number().min(0, 'Les frais de port ne peuvent pas être négatifs.').default(0),
  tvaPct: z
    .number()
    .min(0, 'Le taux de TVA ne peut pas être négatif.')
    .max(100, 'Taux de TVA invalide.')
    .nullable()
    .optional(),
  notes: z.string().trim().max(1000, 'Notes trop longues (1000 caractères max).').nullable().optional(),
  lignes: z.array(ligneFactureSchema).max(200, 'Trop de lignes (200 maximum).').default([]),
})
export type FactureCreateInput = z.infer<typeof factureCreateSchema>

// Mise à jour d'un BROUILLON uniquement (le service refuse toute facture émise/payée/annulée).
// Mêmes champs, tous optionnels ; les lignes remplacent en bloc si fournies. Pas de `statut`.
export const factureUpdateSchema = z.object({
  clientId: z.string().uuid('Client invalide.').nullable().optional(),
  dateFacture: dateISO.optional(),
  dateEcheance: dateISO.nullable().optional(),
  remise: z.number().min(0).max(100).optional(),
  fraisPort: z.number().min(0).optional(),
  tvaPct: z.number().min(0).max(100).nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
  lignes: z.array(ligneFactureSchema).max(200).optional(),
})
export type FactureUpdateInput = z.infer<typeof factureUpdateSchema>

// Statuts cibles autorisés via la route de changement de statut (hors émission, qui a sa
// propre route). On ne « revient » jamais à brouillon (inaltérabilité).
export const FACTURE_TRANSITIONS = ['payee', 'annulee'] as const
export type FactureTransition = (typeof FACTURE_TRANSITIONS)[number]

export const factureStatutSchema = z.object({
  statut: z.enum(FACTURE_TRANSITIONS),
})
export type FactureStatutInput = z.infer<typeof factureStatutSchema>
